import Table from "@/common/elements/Table";
import {
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
} from "@/common/styles";
import { useEffect, useState } from "react";
import Button from "@/common/elements/Button";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  StopOutlined,
} from "@ant-design/icons";
import ClassificationInfoForm from "./ClassificationInfoForm";
import { useDispatch, useSelector } from "react-redux";
import MedicalQuestionnaireModal from "@/components/InmateDetails/MedicalQuestionnaireModal";
import {
  getOptionsFromRefList,
  getRefListNameByID,
  hasCheckSecurity,
  isNonEmptyVariable,
} from "@/util/common_function.js";
import { v4 as uuidV4 } from "uuid";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice.js";
import { Form, Popconfirm } from "antd";
import dayjs, { isDayjs } from "dayjs";
import API_ENDPOINTS from "@/util/api";
import api from "@/services/axios_custom";
import _ from "lodash";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";

const ClassificationQuestionnairesList = () => {
  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state?.bookings
  );
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [
    inmateClassificationQuestionnaires,
    setInmateClassificationQuestionnaires,
  ] = useState([]);
  const [singleInmateClassification, setSingleInmateClassification] =
    useState(null);
  const [isModalShown, setIsModalShown] = useState(false);
  const [isEdit, setisEdit] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [questionnaireLoading, setQuestionnaireLoading] = useState(false);
  const [classificationsQuestionnaire, setClassificationsQuestionnaire] =
    useState(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userDetails"));
    if (isNonEmptyVariable(user)) {
      setUserDetails(user);
    }
    getQuestionnaireListFromAPI();
  }, []);

  useEffect(() => {
    if (bookings !== null) {
      let inmateClassificationQuestionnairesCopy =
        bookings?.inmateClassificationQuestionnaires;
      inmateClassificationQuestionnairesCopy =
        inmateClassificationQuestionnairesCopy?.map((i) => {
          return {
            ...i,
            totalPoints: i?.questionnaireInstances?.[0]?.totalPoints,
            classification: getRefListNameByID(
              "Classifications",
              i?.classificationID
            ),
            creatorStaff: getRefListNameByID("", i?.creatorStaffID, true),
            uuid: uuidV4(),
            name: "Classification",
          };
        });

      if (isNonEmptyVariable(inmateClassificationQuestionnairesCopy)) {
        setInmateClassificationQuestionnaires(
          () => inmateClassificationQuestionnairesCopy
        );
      } else {
        setInmateClassificationQuestionnaires(() => []);
      }

      if (isNonEmptyVariable(bookings?.arrestReports)) {
        const ar = bookings?.arrestReports?.[0];
        form.setFieldsValue({
          housedForAsOf: isNonEmptyVariable(ar?.housedForAsOf)
            ? dayjs(ar?.housedForAsOf)
            : null,
          needsClassification: ar?.needsClassification,
          nextClassification: isNonEmptyVariable(ar?.nextClassification)
            ? dayjs(ar?.nextClassification)
            : null,
          inmateWorkerEvaluation: ar?.inmateWorkerEvaluation,
        });
      }
    } else {
      setInmateClassificationQuestionnaires(() => []);
      form.setFieldsValue({
        housedForAsOf: null,
        needsClassification: null,
        nextClassification: null,
        inmateWorkerEvaluation: null,
      });
    }
  }, [bookings]);

  const getQuestionnaireListFromAPI = async () => {
    try {
      setQuestionnaireLoading(() => true);
      const url = API_ENDPOINTS.config.questionnaire.questionnairesList;
      const { data } = await api.get(url);
      if (data?.length) {
        let filtered = data?.find((d) => d?.questionnaireID === 5);

        filtered = {
          ...filtered,
          questionGroups: filtered?.questionGroups?.map((qg) => {
            return {
              ...qg,
              questions: qg?.questions?.filter((q) => q?.enabled),
            };
          }),
        };

        setClassificationsQuestionnaire(() => filtered);
      }
    } catch (error) {
      setClassificationsQuestionnaire(() => null);
    } finally {
      setQuestionnaireLoading(() => false);
    }
  };

  const onModalShow = () => {
    setIsModalShown(() => true);
  };

  const onAddQuestionnaireClick = () => {
    setQuestionnaire(classificationsQuestionnaire);
    cancelAction();
    onModalShow();
  };

  const onEditQuestionnaireClick = () => {
    setQuestionnaire(classificationsQuestionnaire);
    setisEdit(true);
    onModalShow();
  };

  const getAnswer = (result) => {
    let resultCopy = _.cloneDeep(result);

    let classificationQuestionnaires = inmateClassificationQuestionnaires;

    if (isNonEmptyVariable(resultCopy)) {
      resultCopy = {
        ...resultCopy,
        questionAnswers: resultCopy?.questionAnswers?.map((q) => {
          if (isDayjs(q.answer)) {
            q.answer = dayjs(q.answer)?.format("MM/DD/YYYY");
          }
          return q;
        }),
      };
    }
    const custodyLevelID = resultCopy?.questionAnswers?.find(
      (q) => q.questionId === 201
    )?.answer;

    const custodyName = getRefListNameByID(
      "Class_Custody_Level",
      custodyLevelID
    );

    const classificationList = getOptionsFromRefList(
      "Classifications",
      false,
      false,
      true
    );

    let classificationID = "";
    if (classificationList?.length && custodyName) {
      classificationList.forEach((c) => {
        if (custodyName.includes(c?.label)) {
          classificationID = c?.value || "";
        }
      });
    }

    const classification = {
      bookingID: bookings?.bookingId,
      creatorStaffID: userDetails?.staffId,
      inmateClassificationQuestionnaireID:
        singleInmateClassification?.inmateClassificationQuestionnaireID || null,
      classificationID: classificationID || "",
      classificationEffectiveDate: resultCopy?.questionAnswers?.find(
        (q) => q.questionId === 205
      )?.answer,
      questionnaireInstances: [resultCopy],
    };

    if (isEdit) {
      // classificationQuestionnaires = classificationQuestionnaires?.map((cq) => {
      //   if (cq?.uuid === singleInmateClassification?.uuid) {
      //     return classification;
      //   }
      //   return cq;
      // });

      classificationQuestionnaires = classificationQuestionnaires?.map((cq) => {
        if (cq?.uuid === singleInmateClassification?.uuid) {
          return {
            ...classification,
            questionnaireInstances: classification?.questionnaireInstances?.map(
              (qi) => {
                return {
                  ...qi,
                  questionnaireInstanceId:
                    singleInmateClassification?.questionnaireInstances?.[0]
                      ?.questionnaireInstanceId,
                  questionAnswers: qi?.questionAnswers?.map((qa) => {
                    const question =
                      singleInmateClassification?.questionnaireInstances?.[0]?.questionAnswers?.find(
                        (q) => q?.questionId === qa?.questionId
                      );

                    return {
                      ...question,
                      ...qa,
                      answerDate: dayjs()?.format("MM/DD/YYYY"),
                    };
                  }),
                };
              }
            ),
          };
        }
        return cq;
      });
    } else {
      classificationQuestionnaires.push(classification);
    }

    let nextClassification = null;
    if (isNonEmptyVariable(classification?.classificationEffectiveDate)) {
      nextClassification = dayjs(classification?.classificationEffectiveDate)
        .add(3, "month")
        ?.format("MM/DD/YYYY");
    }

    const ar = [
      {
        ...bookings?.arrestReports?.[0],
        housedForAsOf: classification?.classificationEffectiveDate,
        nextClassification: nextClassification,
      },
    ];

    dispatch(
      setBookings({
        arrestReports: ar,
        inmateClassificationQuestionnaires: classificationQuestionnaires,
      })
    );

    cancelAction();
  };

  function isValidDateRegex(dateString) {
    const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    return regex.test(dateString);
  }

  const onRowClick = (val) => {
    console.log({val})
    const newVal = {
      ...val,
      questionnaireInstances: val?.questionnaireInstances?.map((item) => {
        return {
          ...item,
          questionAnswers: item?.questionAnswers?.map((qa) => {
            if (
              isNonEmptyVariable(qa?.answer) &&
              isValidDateRegex(qa?.answer)
            ) {
              return {
                ...qa,
                answer: dayjs(qa?.answer),
              };
            } else {
              return qa;
            }
          }),
        };
      }),
    };
    console.log({newVal},"booking")
    setSingleInmateClassification(newVal);
  };

  const cancelAction = () => {
    setSingleInmateClassification(null);
    setisEdit(false);
    setIsModalShown(false);
  };

  const deleteClassification = () => {
    const icq = inmateClassificationQuestionnaires?.filter(
      (icq) => icq?.uuid !== singleInmateClassification?.uuid
    );
    dispatch(
      setBookings({
        inmateClassificationQuestionnaires: icq,
      })
    );

    if (
      isNonEmptyVariable(
        singleInmateClassification?.inmateClassificationQuestionnaireID
      )
    ) {
      const url =
        API_ENDPOINTS.inmateClassificationQuestionnaires +
        singleInmateClassification?.inmateClassificationQuestionnaireID;
      dispatch(setDeleteAPis(url));
    }

    cancelAction();
  };

  const updateForm = (val) => {
    const name = val?.[0]?.name?.[0];
    const value = val?.[0]?.value;
    let obj = {};
    if (name === "housedForAsOf" || name === "nextClassification") {
      obj[name] = isNonEmptyVariable(value)
        ? dayjs(value)?.format("MM/DD/YYYY")
        : null;
    } else {
      obj[name] = value;
    }

    dispatch(
      setBookings({
        arrestReports: [
          {
            ...bookings?.arrestReports?.[0],
            ...obj,
          },
        ],
      })
    );
  };

  return (
    <>
      <Form form={form} onFieldsChange={updateForm}>
        <ClassificationInfoForm bookingsFieldsEnabled={bookingsFieldsEnabled} />
      </Form>
      <StyledSectionHeading>
        <span>Classification Questionnaires</span>
      </StyledSectionHeading>
      <StyledGridRow justify="end">
        <StyledGridCol style={{ paddingBottom: "10px" }}>
          <StyledFlex align="center" gap="10px">
            <Button
              loading={questionnaireLoading}
              onClick={onAddQuestionnaireClick}
              disabled={
                !bookingsFieldsEnabled ||
                !hasCheckSecurity(
                  Security_Types.functionality,
                  Entity_types.subModule,
                  LABELS_CAPITAL.ADD,
                  MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                  SUB_MODULES_LIST_WITH_ID.Arrest_Report
                )
              }
              icon={<PlusOutlined />}
            >
              Add
            </Button>
            <Button
              onClick={onEditQuestionnaireClick}
              disabled={singleInmateClassification === null}
              icon={<EditOutlined />}
            >
              Details
            </Button>
            <Popconfirm
              title="Delete the classification"
              description="Are you sure to delete this classification?"
              onConfirm={deleteClassification}
              onCancel={() => {}}
              okText="Yes"
              cancelText="No"
            >
              <Button
                color="primary"
                type="primary"
                icon={<DeleteOutlined />}
                disabled={
                  singleInmateClassification === null ||
                  !bookingsFieldsEnabled ||
                  !hasCheckSecurity(
                    Security_Types.functionality,
                    Entity_types.subModule,
                    LABELS_CAPITAL.DELETE,
                    MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                    SUB_MODULES_LIST_WITH_ID.Arrest_Report
                  )
                }
              >
                Delete
              </Button>
            </Popconfirm>

            <Button
              onClick={cancelAction}
              disabled={singleInmateClassification === null}
              icon={<StopOutlined />}
            >
              Cancel
            </Button>
          </StyledFlex>
        </StyledGridCol>
      </StyledGridRow>

      {hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.subModule,
        LABELS_CAPITAL.VIEW,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
        SUB_MODULES_LIST_WITH_ID.Arrest_Report
      ) && (
        <Table
          headers={[
            { key: "classificationEffectiveDate", text: "Effective Date" },
            { key: "classification", text: "Classification" },
            { key: "totalPoints", text: "Total Points" },
            {
              key: "creatorStaff",
              text: "Who filled out the questionnaire?",
            },
            { key: "name", text: "Name" },
          ]}
          data={inmateClassificationQuestionnaires}
          onRowClick={onRowClick}
          selected={singleInmateClassification === null}
        />
      )}

      {isModalShown && (
        <MedicalQuestionnaireModal
          show={isModalShown}
          onHide={cancelAction}
          questionnaire={questionnaire}
          getAnswer={getAnswer}
          singleQuestionnaire={singleInmateClassification}
          isEdit={isEdit}
        />
      )}
    </>
  );
};
export default ClassificationQuestionnairesList;
